import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ProspectListOptions,
  ProspectItem,
} from "@shared-core/data/adapter";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { formatCount } from "@shared-core/domain/formatters";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import { PersonCard } from "../app/components/domain/contacts/PersonCard";

const PAGE_SIZE = 25;

function prospectToPersonData(prospect: ProspectItem) {
  return {
    id: prospect.id,
    name: prospect.name,
    jobTitle: prospect.likely_title,
    rationale: prospect.rationale,
    linkedinUrl: prospect.linkedin_url,
  };
}

export function ProspectsPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") ?? undefined;

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [customerName, setCustomerName] = useState<string | null>(null);
  useEffect(() => {
    if (!customerFilter) {
      setCustomerName(null);
      return;
    }
    dataAdapter
      .getCustomerById(customerFilter)
      .then((c) => {
        if (c) setCustomerName(c.customer.name);
      })
      .catch(() => {});
  }, [dataAdapter, customerFilter]);

  const { state, retry } = useListQuery(() => {
    const options: ProspectListOptions = { page, pageSize: PAGE_SIZE };
    if (customerFilter) options.customerId = customerFilter;
    if (q.trim()) options.q = q.trim();
    return dataAdapter.listProspects(options);
  }, [dataAdapter, q, page, customerFilter]);

  useEffect(() => {
    setPage(1);
  }, [q, customerFilter]);

  const prospects: ProspectItem[] =
    state.status === "success" ? state.data.items : [];
  const total = state.status === "success" ? state.data.total : 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl mb-1">
          {customerFilter
            ? `Prospects for ${customerName ?? "…"}`
            : "Prospects"}
        </h1>
        {customerFilter ? (
          <CustomerScopeChip
            customerName={customerName}
            onDismiss={handleDismissCustomerScope}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Suggested contacts across your portfolio
          </p>
        )}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search prospects..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="px-3 py-2 border border-border rounded bg-background text-sm w-full sm:w-64"
      />

      {/* Status / results count */}
      <div className="text-sm text-muted-foreground">
        {state.status === "loading"
          ? "Loading..."
          : state.status === "success"
            ? `${formatCount(total)} prospect${total !== 1 ? "s" : ""}`
            : null}
      </div>

      {/* Error */}
      {state.status === "error" && (
        <ErrorPanel message={state.message} onRetry={retry} />
      )}

      {/* Empty */}
      {state.status === "success" && prospects.length === 0 && (
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          No suggested contacts yet. Run enrichment on a customer to discover
          prospects.
        </div>
      )}

      {/* Results */}
      {state.status === "success" && prospects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {prospects.map((prospect) => (
            <PersonCard
              key={prospect.id}
              person={prospectToPersonData(prospect)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
